// src/pages/merchant/Kyc.js
// Vérification KYC du marchand — personne physique / personne morale (société)
// Maxula

import React, { useEffect, useState, useRef } from 'react';
import { FiUpload, FiShield, FiCheckCircle, FiClock, FiXCircle, FiSend, FiUser, FiBriefcase } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { verificationAPI } from '../../services/api';

const STATUS = {
  none: { label: 'Non soumis', cls: 'bg-gray-100 text-gray-600', icon: FiShield },
  pending: { label: 'En attente', cls: 'bg-yellow-100 text-yellow-700', icon: FiClock },
  verified: { label: 'Vérifié', cls: 'bg-green-100 text-green-700', icon: FiCheckCircle },
  rejected: { label: 'Refusé', cls: 'bg-red-100 text-red-700', icon: FiXCircle }
};

// Champs requis/optionnels selon le type d'entité
const NUMBER_FIELDS = {
  physique: [
    { key: 'cinNumber', label: 'N° CIN', required: true },
    { key: 'iceNumber', label: 'N° ICE (optionnel)', required: false },
    { key: 'patenteNumber', label: 'N° Patente (optionnel)', required: false }
  ],
  morale: [
    { key: 'cinNumber', label: 'CIN du gérant', required: true },
    { key: 'rcNumber', label: 'N° RC', required: true },
    { key: 'iceNumber', label: 'N° ICE', required: true },
    { key: 'ifNumber', label: 'Identifiant Fiscal (IF)', required: true },
    { key: 'patenteNumber', label: 'N° Patente (optionnel)', required: false }
  ]
};
const DOC_FIELDS = {
  physique: [
    { key: 'cin', label: 'CIN', required: true },
    { key: 'patente', label: 'Patente (optionnel)', required: false }
  ],
  morale: [
    { key: 'cin', label: 'CIN du gérant', required: true },
    { key: 'registreCommerce', label: 'Registre de commerce (RC)', required: true },
    { key: 'identificationFiscale', label: 'Attestation ICE / IF', required: true },
    { key: 'patente', label: 'Patente (optionnel)', required: false }
  ]
};

// Catégorie commerciale du compte (merchant.businessType)
const BT_LABEL = { merchant: 'Marchand', promoteur: 'Promoteur immobilier', agence: 'Agence' };
// La forme juridique par défaut, déduite de la catégorie commerciale
const defaultEntityFor = (bt) => (bt === 'agence' || bt === 'promoteur') ? 'morale' : 'physique';

const MerchantKyc = () => {
  const [kyc, setKyc] = useState(null);
  const [entityType, setEntityType] = useState('physique');
  const [businessType, setBusinessType] = useState('merchant');
  const [nums, setNums] = useState({ cinNumber: '', rcNumber: '', iceNumber: '', ifNumber: '', patenteNumber: '' });
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const refs = {
    cin: useRef(), registreCommerce: useRef(),
    identificationFiscale: useRef(), patente: useRef()
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await verificationAPI.getMyKyc();
        const k = res.data?.data?.kyc || {};
        const bt = res.data?.data?.businessType || 'merchant';
        setKyc(k);
        setBusinessType(bt);
        // Forme juridique : on respecte le choix déjà enregistré, sinon on déduit
        // intelligemment de la catégorie (agence/promoteur -> morale).
        setEntityType(
          k.entityType === 'morale' || k.entityType === 'physique'
            ? k.entityType
            : defaultEntityFor(bt)
        );
        setNums({
          cinNumber: k.cinNumber || '', rcNumber: k.rcNumber || '',
          iceNumber: k.iceNumber || '', ifNumber: k.ifNumber || '',
          patenteNumber: k.patenteNumber || ''
        });
      } catch (e) { toast.error('Erreur de chargement'); }
      finally { setLoading(false); }
    })();
  }, []);

  const locked = kyc?.status === 'verified';
  const numberFields = NUMBER_FIELDS[entityType];
  const docFields = DOC_FIELDS[entityType];

  const submit = async () => {
    // Validation client (miroir du backend)
    const missing = [];
    numberFields.forEach((f) => { if (f.required && !String(nums[f.key] || '').trim()) missing.push(f.label); });
    docFields.forEach((f) => { if (f.required && !files[f.key] && !kyc?.documents?.[f.key]) missing.push(`${f.label} (document)`); });
    if (missing.length) { toast.error(`Champs requis : ${missing.join(', ')}`); return; }

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('entityType', entityType);
      Object.entries(nums).forEach(([k, v]) => fd.append(k, v));
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
      const res = await verificationAPI.submit(fd);
      setKyc(res.data?.data);
      toast.success('Documents soumis pour vérification');
    } catch (e) { toast.error(e?.response?.data?.message || 'Erreur lors de la soumission'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;

  const st = STATUS[kyc?.status || 'none'];
  const StIcon = st.icon;

  const TYPE_TABS = [
    { id: 'physique', label: 'Personne physique', desc: 'Auto-entrepreneur / individu', icon: FiUser },
    { id: 'morale', label: 'Société (personne morale)', desc: 'SARL, SA, SAS…', icon: FiBriefcase }
  ];

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Vérification (KYC)</h1>
        <span className={`px-3 py-1 rounded-full text-sm font-semibold inline-flex items-center gap-1 ${st.cls}`}>
          <StIcon size={14} /> {st.label}
        </span>
      </div>

      {kyc?.status === 'rejected' && kyc?.rejectReason && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">Motif du refus : {kyc.rejectReason}</div>
      )}
      {kyc?.status === 'verified' && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm inline-flex items-center gap-2">
          <FiCheckCircle /> Votre compte est vérifié. Le badge apparaît sur votre profil.
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
        {/* Catégorie de compte (lecture seule) */}
        <div className="flex items-center justify-between rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
          <div className="text-sm">
            <div className="text-gray-500 text-xs">Catégorie de compte</div>
            <div className="font-semibold text-gray-900">{BT_LABEL[businessType] || 'Marchand'}</div>
          </div>
          {(businessType === 'agence' || businessType === 'promoteur') && (
            <span className="text-xs text-primary-600 bg-primary-50 border border-primary-100 rounded-full px-3 py-1">
              Forme juridique conseillée : personne morale
            </span>
          )}
        </div>

        {/* Type d'entité */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Type d'entité</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TYPE_TABS.map((t) => {
              const TIcon = t.icon;
              const active = entityType === t.id;
              return (
                <button key={t.id} type="button" disabled={locked}
                  onClick={() => setEntityType(t.id)}
                  className={`flex items-start gap-3 p-3 rounded-xl border text-left transition ${
                    active ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-300' : 'border-gray-200 hover:bg-gray-50'
                  } ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <span className={`mt-0.5 ${active ? 'text-primary-600' : 'text-gray-400'}`}><TIcon size={20} /></span>
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">{t.label}</span>
                    <span className="block text-xs text-gray-500">{t.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {entityType === 'morale'
              ? 'Société : RC, ICE et Identifiant Fiscal requis, plus la CIN du gérant.'
              : 'Personne physique : CIN requise. ICE et Patente sont optionnels.'}
          </p>
        </div>

        {/* Numéros */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {numberFields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium mb-1">
                {f.label}{f.required && <span className="text-red-500"> *</span>}
              </label>
              <input
                value={nums[f.key] || ''}
                disabled={locked}
                onChange={(e) => setNums((n) => ({ ...n, [f.key]: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-50" />
            </div>
          ))}
        </div>

        {/* Documents */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {docFields.map((d) => {
            const hasPrev = !!kyc?.documents?.[d.key];
            return (
              <div key={d.key} className="border rounded-lg p-3 flex items-center justify-between gap-2">
                <div className="text-sm min-w-0">
                  <div className="font-medium text-gray-800">
                    {d.label}{d.required && <span className="text-red-500"> *</span>}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {files[d.key]?.name || (hasPrev ? 'Document déjà fourni' : 'PDF ou image')}
                  </div>
                </div>
                <button type="button" onClick={() => refs[d.key].current?.click()} disabled={locked}
                  className="shrink-0 px-3 py-1.5 rounded-lg border text-sm flex items-center gap-1 hover:bg-gray-50 disabled:opacity-50">
                  <FiUpload size={14} /> Choisir
                </button>
                <input ref={refs[d.key]} type="file" accept="image/*,.pdf" className="hidden"
                  onChange={(e) => setFiles((f) => ({ ...f, [d.key]: e.target.files?.[0] || null }))} />
              </div>
            );
          })}
        </div>

        {!locked && (
          <button onClick={submit} disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60">
            <FiSend /> {submitting ? 'Envoi…' : 'Soumettre pour vérification'}
          </button>
        )}
      </div>
    </div>
  );
};

export default MerchantKyc;
